import React, { useCallback, useMemo } from 'react';
import { greenColor } from './PieChart';
import { TLPieChartData } from './TwoLevelPieChart';
import { convertDate23, formatN } from 'utils';
import { useWindowSize } from 'pages/Logs/hooks';
import { chartRes1 } from 'pages/Metrics/components/Charts.styled';
import * as S from './hooks.styled';
import { getAreaSeriesColor } from './AreaChart.styled';
import { useAppSelector } from 'redux/hooks';
import { cashColor, redColor } from 'pages/Logs/components/LogTable.styled';
import { purpleColor } from './PieChart.styled';
import { useMetrics } from 'pages/Metrics/hooks';
import { selectCurrency, selectOrganizationData } from 'pages/redux';
import { logGroupsRaw } from 'pages/Logs/Logs';
import { metricsBucketName } from 'pages/Logs/logUtils';
import { metricsChartSeriesKeys, metricsSeriesScopeLabel } from 'pages/Metrics/metricsScopeCharts';
import { selectActivityLogsTypeAttributeMode } from 'pages/Logs/redux';

export type PieChartLogData1 = {
  name: string,
  value: number,
  ind?: number,
}

export type OtherField = {
  name: string,
  value: number
}

export type PieChartLogData = {
  name: string,
  value: number,
  ind?: number,
  total: number,
  otherFields: OtherField[]
}

export type AreaChartData = {
  date: string,
  dateF: Date
  total: number,
  daily: number
}

export type SimpleLineData = Record<string, string | number | Date>;
export type SimpleLineChart = {
  chartData: SimpleLineData[],
  typeTotalValues: Record<string, number>
};

export const getDayEnd = (date: Date) => {
  date.setHours(23);
  date.setMinutes(59);
  date.setSeconds(59);
  date.setMilliseconds(999);
  return date;
};

// For daily area chart
export const getDayStart = (date: Date | number) => {
  date = new Date(date);
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
};

type ChartDataContextValue = {
  getPieChartData: (
    charge?: '-' | '+',
    inventoryMode?: string | null,
  ) => PieChartLogData[],
  getAreaChartData: (isUseRaw?: true) => AreaChartData[],
  getSimpleLineData: () => SimpleLineChart,
};

const ChartDataContext = React.createContext<ChartDataContextValue | null>(null);

type LineLog = {
  id: string,
  type: string,
  date: string,
  value: string,
  count: number,
};

const parseChartLogTime = (date: string) => {
  const time = Date.parse(date);
  return Number.isNaN(time) ? 0 : time;
};

/** Sort once, then merge into the last day — logs are chronological so no findIndex. */
const appendOrMergeDay = <T extends { date: number }>(
  days: T[],
  logTime: number,
  createDay: () => T,
  mergeDay: (last: T) => T,
) => {
  const last = days[days.length - 1];
  if (last && logTime <= last.date) {
    days[days.length - 1] = mergeDay(last);
    return;
  }
  days.push(createDay());
};

function useChartDataCompute(): ChartDataContextValue {
  const {
    checkFilteredLogs, logGroups, getFlowTypeValue, checkedMetric, staffMaps,
    chartTime, metricsScopeSelection, activityGroupsModel,
  } = useMetrics();
  const organizationData = useAppSelector(selectOrganizationData);
  const activityLogsTypeAttributeMode = useAppSelector(selectActivityLogsTypeAttributeMode);
  const inventoryItemNames = React.useMemo(() => new Set(
    (Array.isArray(organizationData?.inventory) ? organizationData!.inventory! : [])
      .map((i) => i.name)
      .filter(Boolean),
  ), [organizationData?.inventory]);

  const seriesName = useCallback((log: typeof checkFilteredLogs[number], inventoryMode?: string | null) => {
    if (checkedMetric.name === 'Employees') {
      return metricsBucketName(
        log,
        checkedMetric.name,
        inventoryMode,
        inventoryItemNames,
        checkedMetric.checked,
        staffMaps,
        activityLogsTypeAttributeMode,
        logGroups,
      );
    }
    return metricsSeriesScopeLabel(log, metricsScopeSelection, activityGroupsModel);
  }, [
    checkedMetric.name,
    checkedMetric.checked,
    inventoryItemNames,
    staffMaps,
    activityLogsTypeAttributeMode,
    logGroups,
    metricsScopeSelection,
    activityGroupsModel,
  ]);

  const simpleLineData = useMemo((): SimpleLineChart => {
    const endDate = checkFilteredLogs.length
      ? new Date(checkFilteredLogs[checkFilteredLogs.length - 1].date)
      : new Date(chartTime?.endDate || new Date());
    const startDate = checkFilteredLogs.length
      ? new Date(checkFilteredLogs[0].date)
      : new Date(chartTime?.startDate || new Date());

    const logTypeValues: Record<string, number> = {};
    const seriesKeys = checkedMetric.name === 'Employees'
      ? checkedMetric.checked
      : metricsChartSeriesKeys(metricsScopeSelection, activityGroupsModel);
    seriesKeys.forEach((logType) => {
      logTypeValues[logType] = 0;
    });

    type ChartDataBuild = {
      values: { [x: string]: number },
      date: number
    }

    const forceLogs: LineLog[] = [
      {
        id: 'xxx2',
        type: '',
        date: getDayEnd(startDate).toString(),
        value: '0',
        count: 0,
      },
      {
        id: 'xxx1',
        type: '',
        date: getDayEnd(endDate).toString(),
        value: '0',
        count: 0,
      },
    ];

    const rows: LineLog[] = [...checkFilteredLogs, ...forceLogs];
    rows.sort((a, b) => parseChartLogTime(a.date) - parseChartLogTime(b.date));

    const chartDataBuild: ChartDataBuild[] = [];
    const typeTotalValues: Record<string, number> = {};

    const finalizeDayTotal = (day: ChartDataBuild) => {
      let total = 0;
      Object.entries(day.values).forEach(([k, v]) => {
        if (k !== 'total') total += v;
      });
      day.values.total = total;
    };

    rows.forEach((log) => {
      const logTime = parseChartLogTime(log.date);
      const series = log.type
        ? seriesName(log as typeof checkFilteredLogs[number])
        : '';
      const flowValue = log.type ? getFlowTypeValue(log as typeof checkFilteredLogs[number]) : 0;
      if (series) {
        typeTotalValues[series] = (typeTotalValues[series] || 0) + flowValue;
      }

      appendOrMergeDay(
        chartDataBuild,
        logTime,
        () => {
          const last = chartDataBuild[chartDataBuild.length - 1];
          if (last) finalizeDayTotal(last);
          const prior = last
            ? { ...last.values }
            : { ...logTypeValues };
          delete prior.total;
          if (series) {
            prior[series] = (prior[series] || 0) + flowValue;
          }
          return {
            date: +getDayEnd(new Date(log.date)),
            values: prior,
          };
        },
        (last) => {
          if (!series) return last;
          last.values[series] = (last.values[series] || 0) + flowValue;
          return last;
        },
      );
    });

    if (chartDataBuild.length > 0) {
      finalizeDayTotal(chartDataBuild[chartDataBuild.length - 1]);
    }

    const chartData: SimpleLineData[] = chartDataBuild.map((b) => ({
      name: convertDate23(new Date(b.date)),
      date: new Date(b.date),
      ...b.values,
    }));

    Object.entries(typeTotalValues).forEach(([k, v]) => {
      if (!v) {
        chartData.forEach((d) => {
          delete d[k];
        });
        delete typeTotalValues[k];
      }
    });

    const max = 8;
    const keysSorted = Object.keys(typeTotalValues).sort(
      (a, b) => typeTotalValues[b] - typeTotalValues[a]
    );

    const initialLen = Object.keys(typeTotalValues).length - 1;
    let keys = keysSorted.filter(([,], i) => i < Math.ceil((max - 1) / 2)
      || initialLen - i < Math.trunc((max - 1) / 2));

    const othersCounts: number[] = [];
    const keySet = new Set(keys);
    if (keysSorted.length - keys.length >= 1) {
      chartData.forEach((d) => {
        let othersCount = 0;
        Object.entries(d).forEach(([k, v]) => {
          if (!keySet.has(k) && typeof v === 'number' && k !== 'total') {
            othersCount += v;
          }
        });
        othersCounts.push(othersCount);
      });
    } else {
      keys = keysSorted;
    }

    keys.push('total');
    const keepKeys = new Set(keys);

    Object.entries(typeTotalValues).forEach(([k]) => {
      if (!keepKeys.has(k)) {
        chartData.forEach((d, i) => {
          delete d[k];
          if (othersCounts.length) {
            d.Others = othersCounts[i];
          }
        });
        delete typeTotalValues[k];
      }
    });

    if (othersCounts.length) {
      typeTotalValues.Others = 0;
    }

    const keySorted = Object.keys(typeTotalValues).sort();
    const typeTotalValues1: typeof typeTotalValues = {};
    for (let i = 0; i < keySorted.length; i += 1) {
      typeTotalValues1[keySorted[i]] = typeTotalValues[keySorted[i]];
    }

    return { chartData, typeTotalValues: typeTotalValues1 };
  }, [
    checkFilteredLogs,
    chartTime,
    checkedMetric.checked,
    seriesName,
    getFlowTypeValue,
  ]);

  const buildPieChartData = useCallback((
    charge?: '-' | '+',
    inventoryMode?: string | null,
  ) => {
    let total1 = 0;
    const chartDataByName = new Map<string, PieChartLogData1>();

    checkFilteredLogs.forEach((log) => {
      const bucket = seriesName(log, inventoryMode);
      const flow = getFlowTypeValue(log);
      const value = flow < 0 ? flow * -1 : flow;
      const matchesCharge = !charge
        || (charge === '-' && Number(log.value) < 0)
        || (charge === '+' && Number(log.value) >= 0);
      if (!matchesCharge || value <= 0) return;
      total1 += value;
      const prev = chartDataByName.get(bucket);
      if (prev) {
        prev.value += value;
      } else {
        chartDataByName.set(bucket, { name: bucket, value });
      }
    });

    const pCData0: PieChartLogData[] = Array.from(chartDataByName.values()).map((d) => (
      { ...d, total: total1, otherFields: [] }
    ));

    const total0 = pCData0.reduce((a, b) => a + b.value, 0);
    const toRemove: string[] = [];

    pCData0.sort((a, b) => a.value - b.value);
    pCData0.forEach((d) => {
      if (Math.trunc((d.value / total0) * 100) < 9) {
        if (pCData0.length - toRemove.length > 7) {
          toRemove.push(d.name);
        }
      }
    });

    let pCData1: PieChartLogData[] = [];
    const others: PieChartLogData[] = [];

    if (toRemove.length > 1) {
      let othersTotal = 0;
      const otherFields: OtherField[] = [];
      const removeSet = new Set(toRemove);
      pCData0.forEach((d) => {
        if (!removeSet.has(d.name)) return;
        othersTotal += d.value;
        otherFields.push({ name: d.name, value: d.value });
      });
      pCData1 = pCData0.filter((d) => !removeSet.has(d.name));
      others.push({
        name: 'Others',
        value: othersTotal,
        total: total1,
        otherFields
      });
    } else {
      pCData1 = [...pCData0];
    }

    const total = pCData1.reduce((a, b) => a + (Number(b.value) || 0), 0);
    const pCData11: PieChartLogData[] = total ? pCData1 : [];

    const all: PieChartLogData[] = [];
    if (checkedMetric.name === 'Employees') {
      all.push(...pCData11);
    } else {
      const byName = new Map(pCData11.map((d) => [d.name, d]));
      Object.entries(logGroups).forEach(([groupName, v]) => {
        const dTmp: PieChartLogData[] = [];
        v.forEach((name) => {
          const d = byName.get(name);
          if (d) dTmp.push(d);
        });
        if (groupName === 'Departments') {
          (logGroups.Departments || []).forEach((name) => {
            const d = byName.get(name);
            if (d && !dTmp.includes(d)) dTmp.push(d);
          });
        }
        dTmp.sort((a, b) => {
          if (Number(a.value) < Number(b.value)) return 1;
          if (Number(a.value) > Number(b.value)) return -1;
          return 0;
        });
        if (dTmp.length) all.push(...dTmp);
      });
    }

    const pCData22 = [...all];
    pCData22.sort((a, b) => {
      if (Number(a.value) < Number(b.value)) return 1;
      if (Number(a.value) > Number(b.value)) return -1;
      return 0;
    });

    const allByName = new Map(all.map((a) => [a.name, a]));
    pCData22.forEach((d, i) => {
      const row = allByName.get(d.name);
      if (row) row.ind = i;
    });

    const pCData: PieChartLogData[] = total ? [...all, ...others] : [];
    // A name can sit in more than one log group; two wedges of the same colour
    // look like one pie with a leftover connector at the opposite side.
    const uniqueByName: PieChartLogData[] = [];
    const seenNames = new Set<string>();
    pCData.forEach((d) => {
      if (seenNames.has(d.name)) return;
      seenNames.add(d.name);
      uniqueByName.push(d);
    });
    uniqueByName.sort((a, b) => (a.name > b.name ? 1 : -1));

    return uniqueByName;
  }, [
    checkFilteredLogs,
    seriesName,
    getFlowTypeValue,
    checkedMetric.name,
    logGroups,
  ]);

  const pieChartDataNone = useMemo(() => buildPieChartData(), [buildPieChartData]);
  const pieChartDataMinus = useMemo(() => buildPieChartData('-'), [buildPieChartData]);
  const pieChartDataPlus = useMemo(() => buildPieChartData('+'), [buildPieChartData]);

  const getPieChartData = useCallback((
    charge?: '-' | '+',
    inventoryMode?: string | null,
  ) => {
    if (inventoryMode) return buildPieChartData(charge, inventoryMode);
    if (charge === '-') return pieChartDataMinus;
    if (charge === '+') return pieChartDataPlus;
    return pieChartDataNone;
  }, [
    buildPieChartData,
    pieChartDataMinus,
    pieChartDataPlus,
    pieChartDataNone,
  ]);

  const areaChartData = useMemo(() => {
    const endDate = checkFilteredLogs.length
      ? new Date(checkFilteredLogs[checkFilteredLogs.length - 1].date)
      : new Date(chartTime?.endDate || new Date());
    const startDate = checkFilteredLogs.length
      ? new Date(checkFilteredLogs[0].date)
      : new Date(chartTime?.startDate || new Date());

    type AreaChartDataBuild = {
      date: number,
      total: number,
      daily: number
    }

    const forceLogs: LineLog[] = [
      {
        id: 'xxx1',
        type: '',
        date: getDayEnd(startDate).toString(),
        value: '0',
        count: 0,
      },
      {
        id: 'xxx2',
        type: '',
        date: getDayEnd(endDate).toString(),
        value: '0',
        count: 0,
      },
    ];

    const rows: LineLog[] = [...checkFilteredLogs, ...forceLogs];
    rows.sort((a, b) => parseChartLogTime(a.date) - parseChartLogTime(b.date));

    const areaChartBuild: AreaChartDataBuild[] = [];
    rows.forEach((log) => {
      const logTime = parseChartLogTime(log.date);
      const total0 = log.type ? getFlowTypeValue(log as typeof checkFilteredLogs[number]) : 0;
      const total = log.id ? total0 : 0;

      appendOrMergeDay(
        areaChartBuild,
        logTime,
        () => {
          const priorTotal = areaChartBuild.length > 0
            ? areaChartBuild[areaChartBuild.length - 1].total
            : 0;
          return {
            date: +getDayEnd(new Date(log.date)),
            total: total + priorTotal,
            daily: total,
          };
        },
        (last) => ({
          ...last,
          total: last.total + total,
          daily: last.daily + total,
        }),
      );
    });

    return areaChartBuild.map((d) => ({
      date: convertDate23(new Date(d.date)),
      dateF: new Date(d.date || 0),
      total: d.total,
      daily: d.daily,
    }));
  }, [checkFilteredLogs, chartTime, getFlowTypeValue]);

  const getAreaChartData = useCallback((isUseRaw?: true) => {
    if (!isUseRaw) return areaChartData;
    return areaChartData.map((d) => ({
      ...d,
      date: d.dateF.toISOString(),
    }));
  }, [areaChartData]);

  const getSimpleLineData = useCallback(() => simpleLineData, [simpleLineData]);

  return { getPieChartData, getAreaChartData, getSimpleLineData };
};

export const ChartDataProvider = ({ children }: { children: React.ReactNode }) => {
  const value = useChartDataCompute();
  return React.createElement(ChartDataContext.Provider, { value }, children);
};

export const useChartData = () => {
  const shared = React.useContext(ChartDataContext);
  if (!shared) {
    throw new Error('useChartData must be used within ChartDataProvider');
  }
  return shared;
};

export const useChart = () => {
  const { width: wWidth } = useWindowSize();
  // Active activityLogs maps /logs cash chips onto this flowType; do not read the stored Metrics one.
  const { flowType } = useMetrics();
  const currency = useAppSelector(selectCurrency);

  const tipTone = (value: number): 'plus' | 'minus' | 'neutral' => {
    if (flowType === 'count') return 'neutral';
    if (value < 0) return 'minus';
    if (value > 0) return 'plus';
    return 'neutral';
  };

  const fmtTipValue = (value: number, signed?: boolean) => {
    if (flowType === 'count') return `${value}`;
    const sign = signed ? '-' : '';
    return `${currency}${sign}${Number(value).toLocaleString()}`;
  };

  const customLineChartToolTip = (
    props: any,
  ) => {
    const { payload, active } = props;

    if (active && payload?.length) {
      payload.sort((a: any, b: any) => b.value - a.value, 0);
      const { payload: payload22 } = payload[0];
      const { name } = payload22;
      return (
        <S.Container>
          <S.TipTitle>{name}</S.TipTitle>
          <S.TipStack>
            {
              payload.map((p: any) => {
                const { name: name1, value, stroke } = p;
                return (
                  <S.TipRow key={`lCT_${name1}`}>
                    <S.TipSwatch fill={stroke} />
                    <S.TipLabel>{name1}</S.TipLabel>
                    <S.TipValue tone={tipTone(value)}>
                      {fmtTipValue(value)}
                    </S.TipValue>
                  </S.TipRow>
                );
              })
            }
          </S.TipStack>
        </S.Container>
      );
    }
    return null;
  };

  const customRadarToolTip = (
    props: any,
  ) => {
    const { payload, active } = props;

    if (active && payload?.length) {
      const { payload: payload2 } = payload[0];
      const {
        name, outFlow, inFlow, count
      } = payload2;

      const colors = { outFlow: redColor, inFlow: greenColor, count: purpleColor };
      const grossCalc = flowType === 'count' ? (inFlow - outFlow) < 0 ? (inFlow - outFlow) * -1 : (inFlow - outFlow)
        : (inFlow + outFlow);
      const isGrossRed = flowType === 'count' && (inFlow - outFlow) < 0;
      // Action types without cash flows hide inflow/outflow in the tip.
      const showFlows = !(logGroupsRaw.Actions as readonly string[]).includes(name);

      return (
        <S.Container>
          <S.TipTitle>{name || '—'}</S.TipTitle>
          <S.TipStack>
            {
              (inFlow !== undefined && showFlows) ? (
                <S.TipRow>
                  <S.TipSwatch fill={colors.inFlow} />
                  <S.TipLabel>Inflow</S.TipLabel>
                  <S.TipValue tone={flowType === 'count' ? 'neutral' : 'plus'}>
                    {fmtTipValue(inFlow)}
                  </S.TipValue>
                </S.TipRow>
              ) : null
            }
            {
              (outFlow !== undefined && showFlows) ? (
                <S.TipRow>
                  <S.TipSwatch fill={colors.outFlow} />
                  <S.TipLabel>Outflow</S.TipLabel>
                  <S.TipValue tone={flowType === 'count' ? 'neutral' : 'minus'}>
                    {fmtTipValue(outFlow)}
                  </S.TipValue>
                </S.TipRow>
              ) : null
            }
            {
              (outFlow !== undefined && inFlow !== undefined && showFlows) ? (
                <S.TipRow>
                  <S.TipSwatch fill={(grossCalc < 0 || isGrossRed) ? redColor : cashColor} />
                  <S.TipLabel>Gross</S.TipLabel>
                  <S.TipValue tone={(grossCalc < 0 || isGrossRed) ? 'minus' : 'plus'}>
                    {fmtTipValue(grossCalc)}
                  </S.TipValue>
                </S.TipRow>
              ) : null
            }
            {
              count !== undefined ? (
                <S.TipRow>
                  <S.TipSwatch fill={colors.count} />
                  <S.TipLabel>All</S.TipLabel>
                  <S.TipValue tone="neutral">
                    {fmtTipValue(count)}
                  </S.TipValue>
                </S.TipRow>
              ) : null
            }
          </S.TipStack>
        </S.Container>
      );
    }
    return null;
  };

  const customAreaChartToolTip = (
    props: any,
    { startTotal }: { startTotal: number },
  ) => {
    const { payload, active } = props;

    if (active && payload?.length) {
      const { payload: p2 } = payload[0];
      const { date, daily, total } = p2;
      const dailyNum = Number(daily);
      const totalNum = Number(total);
      const priorTotal = totalNum - dailyNum;
      const startTotalNum = Number(startTotal);
      const dailyColor = getAreaSeriesColor('daily', dailyNum);
      const totalColor = getAreaSeriesColor('total', totalNum);

      const getPctMeta = (value: number, baseline: number) => {
        const canShowPct = baseline !== 0;
        const pctChange = canShowPct
          ? (value / Math.abs(baseline)) * 100
          : 0;
        const isProfit = pctChange > 0;
        const isLoss = pctChange < 0;
        const pctDisplay = !canShowPct
          ? '—'
          : pctChange === 0
            ? '0%'
            : `${isProfit ? '+' : '-'}${Math.abs(pctChange).toFixed(1)}%`;
        return {
          isProfit, isLoss, pctDisplay
        };
      };

      const dailyVsPrior = getPctMeta(dailyNum, priorTotal);
      const dailyVsStart = getPctMeta(dailyNum, startTotalNum);
      const totalVsStart = getPctMeta(totalNum - startTotalNum, startTotalNum);

      const renderVsRow = (
        label: string,
        meta: ReturnType<typeof getPctMeta>,
        seriesColor: string,
        key: string,
      ) => (
        <S.TipRow key={key}>
          <S.TipSwatch fill={seriesColor} />
          <S.TipLabel>{label}</S.TipLabel>
          <S.TipValue tone={meta.isLoss ? 'minus' : meta.isProfit ? 'plus' : 'neutral'}>
            {meta.pctDisplay}
          </S.TipValue>
        </S.TipRow>
      );

      return (
        <S.Container>
          <S.TipTitle>{date}</S.TipTitle>
          <S.TipStack>
            {
              payload.map((p: any) => {
                const {
                  dataKey, payload: payload2, name
                } = p;
                const { [dataKey]: value } = payload2;
                const seriesColor = getAreaSeriesColor(dataKey, value);

                return (
                  <S.TipRow key={`areaTT_${name}`}>
                    <S.TipSwatch fill={seriesColor} />
                    <S.TipLabel>{name}</S.TipLabel>
                    <S.TipValue tone={tipTone(Number(value))}>
                      {fmtTipValue(Number(value))}
                    </S.TipValue>
                  </S.TipRow>
                );
              })
            }
            {
              flowType !== 'count' ? (
                <>
                  {renderVsRow('Daily vs prior', dailyVsPrior, dailyColor, 'dailyVsPrior')}
                  {renderVsRow('Daily vs start', dailyVsStart, dailyColor, 'dailyVsStart')}
                  {renderVsRow('Total vs start', totalVsStart, totalColor, 'totalVsStart')}
                </>
              ) : null
            }
          </S.TipStack>
        </S.Container>
      );
    }
    return null;
  };

  const renderPieToolTip = (
    props: any,
    charge: '-' | '+',
    keyPrefix: string,
  ) => {
    const { payload, active } = props;

    if (!(active && payload?.length)) return null;

    const { name, payload: payload2 } = payload[0];
    // dataKey is showValue (min-slice visual bump) — always prefer the real `value` field
    const value = payload2?.value != null ? payload2.value : payload[0].value;
    const { total, otherFields: oF } = payload2 || {};

    const otherFields: OtherField[] = [...(oF || [])];
    otherFields.sort((a, b) => b.value - a.value);
    const overflow = otherFields.splice(6);
    const signed = charge === '-' || flowType === '-';
    const tone: 'plus' | 'minus' | 'neutral' = flowType === 'count'
      ? 'neutral'
      : signed ? 'minus' : 'plus';
    const pct = total ? Math.round((value / total) * 100) : 0;

    return (
      <S.Container>
        <S.TipHeadRow>
          <S.TipHeadName title={name}>{name}</S.TipHeadName>
          <S.TipPct>{`${pct}%`}</S.TipPct>
        </S.TipHeadRow>
        <S.TipRow>
          <S.TipLabel>Total</S.TipLabel>
          <S.TipValue tone={tone}>
            {fmtTipValue(Number(value), signed && flowType !== 'count')}
          </S.TipValue>
        </S.TipRow>
        {
          otherFields.length ? (
            <S.TipStack>
              {
                otherFields.map((f: OtherField) => {
                  const { value: value1, name: name1 } = f;
                  const childPct = total ? Math.round((value1 / total) * 100) : 0;
                  return (
                    <S.TipSubRow key={`${keyPrefix}_${name1}`}>
                      <S.TipPct>{`${childPct}%`}</S.TipPct>
                      <S.TipLabel>{name1}</S.TipLabel>
                      <S.TipValue tone={tone}>
                        {fmtTipValue(Number(value1), signed && flowType !== 'count')}
                      </S.TipValue>
                    </S.TipSubRow>
                  );
                })
              }
              {overflow.length ? (
                <S.TipMore>{`+${overflow.length} more`}</S.TipMore>
              ) : null}
            </S.TipStack>
          ) : null
        }
      </S.Container>
    );
  };

  const customTwoPieToolTip = (
    props: any,
    { charge }: { charge: '-' | '+' },
  ) => renderPieToolTip(props, charge, 'ofield1');

  const customPieToolTip = (
    props: any,
    { charge }: { charge: '-' | '+' },
  ) => renderPieToolTip(props, charge, 'of2');

  const getPieLabel = ({
    props, pieData, chartId, charge
  } : {
    props: any, pieData: PieChartLogData[] | TLPieChartData[],
    chartId: number, charge: '-' | '+'
  }) => {
    const { index, payload } = props;
    const { value } = payload;
    const pD = [...pieData];
    let pieFontSize = 36 - ((wWidth || 1536) * (16.5 / 1536)) < 0.5 ? '0.5px' :
      `calc(${36 - ((wWidth || 1536) * (16.5 / 1536))}px)`;
    if ((wWidth || 0) < Number(chartRes1.replace('px', ''))) {
      pieFontSize = `calc(${pieFontSize} / 1.5)`;
    }

    return (
      <>
        <svg>
          <defs>
            <text
              {...props}
              id={`pieLabel${chartId}${index}`}
              paintOrder="stroke fill"
              stroke="white"
              strokeWidth={2}
              fontSize={pieFontSize}
              fontWeight={700}
            >
              {`${charge === '-' || flowType === '-' ? '-' : ''}${formatN(value)}`}
            </text>
          </defs>
        </svg>
        {
          index === pD.length - 1 ? (
            <>
              {
                pD.sort((a, b) => a.value - b.value).map((d) => {
                  const index2 = pieData.findIndex((d2) => d2.name === d.name);
                  return (
                    <>
                      <use key={`pieLabel${chartId}${index2}`} href={`#pieLabel${chartId}${index2}`} />
                    </>
                  );
                })
              }
            </>
          ) : (null)
        }
      </>
    );
  };

  return {
    getPieLabel,
    customPieToolTip,
    customTwoPieToolTip,
    customAreaChartToolTip,
    customRadarToolTip,
    customLineChartToolTip
  };
};
